/**
 * Section C — errors, grouped by type (spelling, math, locality, other). Says so plainly
 * when there's nothing to show, rather than leaving a blank section.
 */
import type { ErrorFindingRow, ErrorType, Severity } from '../../reviewer/types';
import { PageLink } from './PageLink';
import { Tile } from '../ui';

const TYPE_LABEL: Record<ErrorType, string> = {
  spelling: 'Spelling',
  math: 'Math',
  locality: 'Locality',
  other: 'Other',
};
const TYPE_ORDER: ErrorType[] = ['spelling', 'math', 'locality', 'other'];

const SEVERITY_META: Record<Severity, { label: string; tone: string }> = {
  high: { label: 'High', tone: 'fail' },
  medium: { label: 'Medium', tone: 'flag' },
  low: { label: 'Low', tone: 'na' },
};

interface ErrorsSectionProps {
  errors: ErrorFindingRow[];
  onJump: (doc: 'report' | 'loi', page: number) => void;
  checked: boolean;
}

export function ErrorsSection({ errors, onJump, checked }: ErrorsSectionProps) {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Errors</h2>
      </div>

      {errors.length === 0 ? (
        <Tile>
          <p className="muted">
            {checked ? 'No errors found in this report.' : 'Awaiting the compliance check.'}
          </p>
        </Tile>
      ) : (
        <div className="stack">
          {TYPE_ORDER.filter((t) => errors.some((e) => e.errorType === t)).map((type) => (
            <Tile key={type} className="error-group">
              <h3 className="checklist-cat__title">{TYPE_LABEL[type]}</h3>
              <div className="error-group__rows">
                {errors
                  .filter((e) => e.errorType === type)
                  .map((e) => {
                    const sev = e.severity ? SEVERITY_META[e.severity] : null;
                    return (
                      <div className="error-row" key={e.id}>
                        <div className="error-row__head">
                          <p className="error-row__desc">{e.description}</p>
                          {sev ? <span className={`mark mark--${sev.tone} pill`}>{sev.label}</span> : null}
                        </div>
                        {e.suggestedFix ? <p className="error-row__fix">Suggested fix: {e.suggestedFix}</p> : null}
                        <PageLink page={e.pageRef} doc="report" onJump={onJump} />
                      </div>
                    );
                  })}
              </div>
            </Tile>
          ))}
        </div>
      )}
    </section>
  );
}
